import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Layers, Maximize2, Minimize2, Plus, ArrowUpDown, AlertTriangle, Loader2,
  Heart, TrendingUp, TrendingDown, AlertCircle, ListChecks, CornerLeftUp,
} from 'lucide-react';
import { loggedGet } from './loggedFetch';
import { HEATMAP_QUERY_OPTIONS } from './cacheConfig';
import { componentLabel } from './componentLabel';
import logger from '../../lib/logger';

/*
 * PenetrationHeatmap — agency × vendor-component grid, color-scaled
 * by spend. The "where does this vendor land?" view at a glance.
 *
 * Three modes for the color scale:
 *   - log    (default): log10($) → 0..1. Compresses dynamic range so
 *            a 1B-account doesn't drown out a 100K-account, which is
 *            critical for spotting white space.
 *   - linear: raw $ / max → 0..1. Honest but visually washed out.
 *   - rank:   percentile rank within non-zero cells. Most useful for
 *            comparing patterns, less for absolute size.
 *
 * Hovering a cell shows agency / component / $ / POs / latest end.
 * Clicking a non-zero cell drills into the existing transactions
 * modal (via the onDrilldown prop the page already threads). Clicking
 * a zero cell calls onSeedPlan with the agency + component context
 * so the user can add it to the 30-60-90 plan as an offensive play.
 */

const COLOR_MODES = [
  { key: 'log', label: 'Log scale' },
  { key: 'linear', label: 'Linear' },
  { key: 'rank', label: 'Rank' },
];

function formatCurrency(num) {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}

function formatPercent(val) {
  if (val == null || isNaN(val)) return '—';
  return `${(val * 100).toFixed(0)}%`;
}

/**
 * RelationshipBadge — shows strategic health of agency relationship
 * Based on tenure_years, growth_cagr, share_of_wallet, at_risk
 */
// Phase 3 (H.29): when the heatmap is scoped to an account list, each agency
// row carries a rollup_role telling the user WHY it's in the list view. The
// most important case is a list member that rolls up to a SCPRS buying-center
// parent — surfaced inline so the user can't miss it.
function RollupBadge({ ag }) {
  if (!ag || !ag.rollup_role) return null;
  if (ag.rollup_role === 'parent') {
    return (
      <span
        className="vca-rollup-badge is-parent"
        title="A SCPRS buying center your list members roll up to."
      >
        buying center
      </span>
    );
  }
  if (ag.rollup_role === 'child') {
    return (
      <span
        className="vca-rollup-badge is-child"
        title={ag.parent_acronym ? `Sub-department under ${ag.parent_acronym}` : 'Sub-department of a list member'}
      >
        sub-dept
      </span>
    );
  }
  // 'member' that rolls up to a parent buying center — the headline case.
  if (ag.parent_acronym) {
    return (
      <span
        className="vca-rollup-badge is-rollup"
        title={`Rolls up to ${ag.parent_official_name || ag.parent_acronym} on SCPRS`}
      >
        <CornerLeftUp size={10} /> {ag.parent_acronym}
      </span>
    );
  }
  return null;
}

function RelationshipBadge({ signals }) {
  if (!signals) return null;

  const { tenure_years, growth_cagr, share_of_wallet, at_risk } = signals;

  // Determine health status
  let status = 'healthy';
  let Icon = Heart;

  if (at_risk) {
    status = 'at-risk';
    Icon = AlertCircle;
  } else if (growth_cagr != null && growth_cagr < -0.1) {
    status = 'declining';
    Icon = TrendingDown;
  } else if (growth_cagr != null && growth_cagr > 0.1) {
    status = 'growing';
    Icon = TrendingUp;
  }

  const tooltipParts = [];
  if (tenure_years != null) tooltipParts.push(`Tenure: ${tenure_years} yr${tenure_years !== 1 ? 's' : ''}`);
  if (growth_cagr != null) tooltipParts.push(`CAGR: ${growth_cagr >= 0 ? '+' : ''}${(growth_cagr * 100).toFixed(0)}%`);
  if (share_of_wallet != null) tooltipParts.push(`Share: ${formatPercent(share_of_wallet)}`);
  if (at_risk) tooltipParts.push('⚠️ At risk');

  return (
    <span
      className={`vca-relationship-badge vca-relationship-badge--${status}`}
      title={tooltipParts.join(' · ')}
    >
      <Icon size={10} />
    </span>
  );
}

// ─── Animation variants for framer-motion ───────────────────────────────────
// Diagonal wave effect: cells animate based on row + column index for a
// cascading reveal from top-left corner.
const CELL_STAGGER_MS = 12;

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.02,
    },
  },
};

const rowVariants = {
  hidden: { opacity: 0 },
  visible: (rowIndex) => ({
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: CELL_STAGGER_MS / 1000,
      delayChildren: rowIndex * 0.025,
    },
  }),
};

const cellVariants = {
  hidden: {
    opacity: 0,
    scale: 0.6,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 25,
    },
  },
};

const headerVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

const legendVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.4,
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

function PenetrationHeatmap({ vendor, includeLowConfidence, strict, productFamily, departmentIds, listName, years, onDrilldown, onSeedPlan }) {
  const [colorMode, setColorMode] = useState('log');
  const [expanded, setExpanded] = useState(false);
  const [hoverCell, setHoverCell] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });

  // Phase 3: when an account list is active, scope the matrix to its
  // departments (+ rollups). `deptKey` keeps the query key stable.
  const deptKey = departmentIds?.length ? departmentIds.join(',') : null;

  const qs = new URLSearchParams();
  if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
  if (!strict) qs.set('strict', 'false');
  if (productFamily) qs.set('product_family', productFamily);
  if (deptKey) qs.set('department_ids', deptKey);
  // Global time filter from TimeFilterContext (null = all-time)
  if (years != null) qs.set('years', String(years));
  qs.set('agencyLimit', expanded ? '120' : '40');
  qs.set('componentLimit', expanded ? '40' : '20');
  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/heatmap?${qs}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'heatmap', vendor, includeLowConfidence, strict, expanded, productFamily, deptKey, years],
    queryFn: loggedGet('PenetrationHeatmap:fetch', path),
    enabled: !!vendor,
    ...HEATMAP_QUERY_OPTIONS,
  });

  // Build a sparse cell lookup keyed `${dept}::${comp}`.
  const cellMap = useMemo(() => {
    const m = new Map();
    for (const c of (data?.cells || [])) {
      m.set(`${c.department_id}::${c.component_id}`, c);
    }
    return m;
  }, [data]);

  // Colors are derived from the chosen scale mode. Returns 0..1 so the
  // CSS can pick a hue from the accent gradient.
  const intensityFor = useMemo(() => {
    if (!data || !data.cells || data.cells.length === 0) return () => 0;
    const max = data.max_cell_spend || 0;
    const nonZero = data.cells.map(c => c.total_spend).filter(v => v > 0).sort((a, b) => a - b);
    return (spend) => {
      if (!spend || spend <= 0 || max <= 0) return 0;
      if (colorMode === 'linear') return Math.min(1, spend / max);
      if (colorMode === 'log') {
        const lo = Math.log10(Math.max(1, nonZero[0] || 1));
        const hi = Math.log10(Math.max(2, max));
        return Math.min(1, Math.max(0, (Math.log10(spend) - lo) / (hi - lo)));
      }
      // rank — percentile within non-zero
      const idx = nonZero.findIndex(v => v >= spend);
      return idx < 0 ? 1 : (idx + 1) / nonZero.length;
    };
  }, [data, colorMode]);

  // Group adjacent same-group_key columns into bands. The backend orders
  // components so groups are already contiguous; we just need the spans
  // for the colspan header + boundary indices to draw the separators.
  // MUST live above any early-return branches (Rules of Hooks) — was the
  // cause of React error #310 on first render before data resolves.
  const componentsForGrouping = data?.components || [];
  const groupBands = useMemo(() => {
    if (!componentsForGrouping.length) return [];
    const bands = [];
    let cur = null;
    componentsForGrouping.forEach((c, idx) => {
      const key = c.group_key || c.category || 'Uncategorized';
      if (!cur || cur.key !== key) {
        cur = { key, label: key, startIdx: idx, colspan: 1 };
        bands.push(cur);
      } else {
        cur.colspan += 1;
      }
    });
    return bands;
  }, [componentsForGrouping]);

  if (isLoading) {
    return (
      <div className="vca-section">
        <div className="vca-section__loading">
          <Loader2 size={14} className="vca-spin" style={{ verticalAlign: 'middle' }} /> Loading penetration heatmap…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="vca-section">
        <div className="vca-section__error">
          <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> Heatmap failed: {error.message}
        </div>
      </div>
    );
  }
  if (!data || data.agencies.length === 0 || data.components.length === 0) {
    // When list-scoped, an empty matrix means the vendor has no recorded spend
    // across the list's departments — say so explicitly so the user doesn't
    // mistake it for missing data. (data?.list_scoped is true only if the
    // server actually applied a department_ids filter.)
    const scopedEmpty = data?.list_scoped || (deptKey && data);
    return (
      <div className="vca-section">
        <h2><Layers size={16} style={{ verticalAlign: 'text-bottom' }} /> Penetration heatmap</h2>
        <p className="vca-section__sub">
          {scopedEmpty
            ? <>No {vendor} spend recorded across <strong>{listName || 'your list'}</strong> (incl. SCPRS rollups). That white space is the expansion opportunity — try turning off the list scope to compare against the full state.</>
            : <>No classified spend yet to map for {vendor}.</>}
        </p>
      </div>
    );
  }

  const { agencies, components, cells } = data;
  const totalCells = agencies.length * components.length;
  const nonZeroCells = cells.length;
  const penetrationPct = totalCells === 0 ? 0 : Math.round((nonZeroCells / totalCells) * 100);

  const isGroupStart = (colIdx) =>
    colIdx === 0 || components[colIdx - 1]?.group_key !== components[colIdx]?.group_key;

  return (
    <motion.div
      className="vca-section vca-heatmap-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <div className="vca-section-head vca-heatmap-head">
        <motion.div
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          <h2>
            <Layers size={16} style={{ verticalAlign: 'text-bottom' }} /> Penetration heatmap · agencies × {vendor}'s products
          </h2>
          {data?.list_scoped && (
            <p className="vca-heatmap-scope">
              <ListChecks size={12} style={{ verticalAlign: 'text-bottom' }} />
              {' '}Scoped to <strong>{listName || 'your list'}</strong> — {agencies.length} agenc{agencies.length === 1 ? 'y' : 'ies'} (incl. SCPRS rollups)
            </p>
          )}
          <p className="vca-section__sub">
            <strong>{nonZeroCells.toLocaleString()}</strong> of {totalCells.toLocaleString()} cells lit up
            ({penetrationPct}% coverage of the displayed matrix).
            White space = expansion opportunity. Click a cell to drill in,
            click an empty cell to seed an offensive play in the 30-60-90 plan.
          </p>
        </motion.div>
        <motion.div
          className="vca-heatmap-controls"
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15, duration: 0.3 }}
        >
          <div className="vca-heatmap-mode" role="tablist">
            <ArrowUpDown size={11} style={{ verticalAlign: 'middle', opacity: 0.6 }} />
            {COLOR_MODES.map(m => (
              <button
                key={m.key}
                role="tab"
                aria-selected={colorMode === m.key}
                className={`vca-heatmap-mode-btn ${colorMode === m.key ? 'is-active' : ''}`}
                onClick={() => setColorMode(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
          <button
            className="vca-add-btn vca-heatmap-expand"
            onClick={() => setExpanded(e => !e)}
            title={expanded ? 'Show top 40 agencies × 20 components' : 'Show top 120 agencies × 40 components'}
          >
            {expanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
            <span>{expanded ? 'Compact' : 'Expand'}</span>
          </button>
        </motion.div>
      </div>

      <div className="vca-heatmap-wrap">
        <table className="vca-heatmap-grid" role="grid">
          <motion.thead
            variants={headerVariants}
            initial="hidden"
            animate="visible"
          >
            {groupBands.length > 1 && (
              <tr className="vca-heatmap-groups-row">
                <th className="vca-heatmap-corner" />
                {groupBands.map(b => (
                  <th
                    key={b.key}
                    colSpan={b.colspan}
                    className="vca-heatmap-group-band"
                    title={`${b.label} — ${b.colspan} component${b.colspan === 1 ? '' : 's'}`}
                  >
                    <span className="vca-heatmap-group-label">{b.label}</span>
                  </th>
                ))}
              </tr>
            )}
            <tr>
              <th className="vca-heatmap-corner" />
              {components.map((comp, colIdx) => (
                <motion.th
                  key={comp.component_id}
                  className={`vca-heatmap-col-head${isGroupStart(colIdx) ? ' is-group-start' : ''}`}
                  title={`${comp.component_name} — ${comp.category || ''} — ${formatCurrency(comp.total_spend)} state-wide`}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + colIdx * 0.015, duration: 0.2 }}
                >
                  <span className="vca-heatmap-col-label">{componentLabel(comp.component_name, vendor)}</span>
                </motion.th>
              ))}
            </tr>
          </motion.thead>
          <motion.tbody
            variants={gridVariants}
            initial="hidden"
            animate="visible"
          >
            {agencies.map((ag, rowIdx) => (
              <motion.tr
                key={ag.department_id}
                variants={rowVariants}
                custom={rowIdx}
              >
                <motion.th
                  className="vca-heatmap-row-head"
                  title={`${ag.official_name || ag.acronym} · vendor spend ${formatCurrency(ag.vendor_spend)} of ${formatCurrency(ag.total_spend)} total`}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + rowIdx * 0.02, duration: 0.25 }}
                >
                  <span className="vca-heatmap-row-label">{ag.acronym}</span>
                  <span className="vca-heatmap-row-spend">{formatCurrency(ag.vendor_spend)}</span>
                  <RollupBadge ag={ag} />
                  <RelationshipBadge signals={ag.relationship_signals} />
                </motion.th>
                {components.map((comp, colIdx) => {
                  const cell = cellMap.get(`${ag.department_id}::${comp.component_id}`);
                  const intensity = intensityFor(cell?.total_spend || 0);
                  const isHovered = hoverCell?.deptId === ag.department_id && hoverCell?.compId === comp.component_id;
                  // Diagonal wave: delay based on row + column position
                  const diagonalDelay = (rowIdx + colIdx) * 0.008;
                  return (
                    <motion.td
                      key={comp.component_id}
                      className={`vca-heatmap-cell ${cell ? 'has-spend' : 'is-empty'} ${isHovered ? 'is-hovered' : ''}${isGroupStart(colIdx) ? ' is-group-start' : ''}`}
                      style={{
                        '--cell-intensity': intensity.toFixed(3),
                      }}
                      variants={cellVariants}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 30,
                        delay: diagonalDelay,
                      }}
                      whileHover={cell ? {
                        scale: 1.15,
                        zIndex: 10,
                        transition: { duration: 0.12 },
                      } : {
                        scale: 1.1,
                        zIndex: 10,
                        transition: { duration: 0.12 },
                      }}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoverCell({
                          deptId: ag.department_id,
                          compId: comp.component_id,
                          ag,
                          comp,
                          cell,
                        });
                        // Viewport-aware placement. The tooltip is normally
                        // centered above the cell; if there's not enough
                        // headroom (top edge cells), flip below. If the
                        // centered x would clip the left or right edge,
                        // nudge x so the (transform: translate(-50%, …))
                        // tooltip stays fully on-screen. Width/height are
                        // estimates that match the CSS (.vca-heatmap-tip
                        // min-width 240, typical height ~180); a few px
                        // off is fine — the goal is to keep the tooltip
                        // ON-screen, not pixel-perfect alignment.
                        const TOOLTIP_W = 280;
                        const TOOLTIP_H = 200;
                        const PAD = 8;
                        const cellTopY = rect.top;
                        const cellBottomY = rect.bottom;
                        const cellCenterX = rect.left + rect.width / 2;
                        const vw = window.innerWidth;
                        const vh = window.innerHeight;
                        // Vertical: prefer above; flip below if no headroom.
                        const hasHeadroom = cellTopY - TOOLTIP_H - PAD - 12 > 0;
                        const yAnchor = hasHeadroom ? cellTopY : cellBottomY;
                        const placeBelow = !hasHeadroom;
                        // Horizontal: keep tooltip's half-width on-screen.
                        const minX = TOOLTIP_W / 2 + PAD;
                        const maxX = vw - TOOLTIP_W / 2 - PAD;
                        const xClamped = Math.max(minX, Math.min(maxX, cellCenterX));
                        // Also guard the bottom-flip from running off the
                        // bottom (rare; happens at small viewports).
                        const finalY = placeBelow
                          ? Math.min(vh - TOOLTIP_H - PAD, yAnchor)
                          : Math.max(PAD + TOOLTIP_H, yAnchor);
                        setHoverPos({ x: xClamped, y: finalY, placeBelow });
                      }}
                      onMouseLeave={() => setHoverCell(null)}
                      onClick={() => {
                        if (cell && onDrilldown) {
                          logger.vendorAnalysis.info('PenetrationHeatmap:drilldown', {
                            vendor, agency: ag.acronym, component: comp.component_name,
                          });
                          onDrilldown({
                            type: 'vendor',
                            value: vendor,
                            department: ag.acronym,
                            departmentId: ag.department_id,
                            component: comp.component_name,
                          });
                        } else if (!cell && onSeedPlan) {
                          logger.vendorAnalysis.info('PenetrationHeatmap:seed-plan', {
                            vendor, agency: ag.acronym, component: comp.component_name,
                          });
                          onSeedPlan({
                            kind: 'heatmap-whitespace',
                            agency: ag.acronym,
                            agency_official_name: ag.official_name,
                            department_id: ag.department_id,
                            target_product: comp.component_name,
                            category: comp.category,
                            play_type: 'offensive',
                            title: `Land ${comp.component_name} at ${ag.acronym}`,
                            rationale: `${ag.acronym} has no recorded ${comp.component_name} spend yet. Total CA-state spend on ${comp.component_name}: ${formatCurrency(comp.total_spend)} across ${comp.agency_count} agencies. Agency total IT spend: ${formatCurrency(ag.total_spend)}.`,
                          });
                        }
                      }}
                    >
                      {cell && intensity > 0.55 && (
                        <span className="vca-heatmap-cell-label">
                          {formatCurrency(cell.total_spend)}
                        </span>
                      )}
                      {!cell && (
                        <span className="vca-heatmap-cell-plus" aria-hidden="true">
                          <Plus size={10} />
                        </span>
                      )}
                    </motion.td>
                  );
                })}
              </motion.tr>
            ))}
          </motion.tbody>
        </table>
      </div>

      {hoverCell && (
        <HeatmapTooltip
          x={hoverPos.x}
          y={hoverPos.y}
          placeBelow={hoverPos.placeBelow}
          ag={hoverCell.ag}
          comp={hoverCell.comp}
          cell={hoverCell.cell}
          vendor={vendor}
        />
      )}

      <Legend max={data.max_cell_spend} colorMode={colorMode} />
    </motion.div>
  );
}

function HeatmapTooltip({ x, y, placeBelow, ag, comp, cell, vendor }) {
  // Two placement modes:
  //   - default: centered above the cell (transform pulls it up via
  //     translate(-50%, -100%) — see PenetrationHeatmap.css)
  //   - placeBelow: cell is near the top of the viewport, no headroom,
  //     so anchor at cell.bottom and pull DOWN via translate(-50%, 0)
  //     plus a small gap.
  // Both modes get the x clamped to keep the centered tooltip on-screen
  // (done at hover-set time in PenetrationHeatmap.js).
  const inlineTransform = placeBelow
    ? 'translate(-50%, 12px)'
    : undefined; // default (translate(-50%, -100%)) lives in CSS
  return (
    <div
      className="vca-heatmap-tip"
      style={{
        left: `${x}px`,
        top: `${placeBelow ? y : y - 12}px`,
        ...(inlineTransform ? { transform: inlineTransform } : {}),
      }}
    >
      <div className="vca-heatmap-tip-head">
        <strong>{ag.acronym}</strong>
        <span className="vca-heatmap-tip-mid"> × </span>
        <strong>{comp.component_name}</strong>
      </div>
      {ag.official_name && (
        <div className="vca-heatmap-tip-sub">{ag.official_name}</div>
      )}
      <div className="vca-heatmap-tip-grid">
        <div>
          <span className="vca-heatmap-tip-label">{vendor} spend</span>
          <span className="vca-heatmap-tip-val">
            {cell ? formatCurrency(cell.total_spend) : 'No spend yet'}
          </span>
        </div>
        {cell && (
          <>
            <div>
              <span className="vca-heatmap-tip-label">PO line items</span>
              <span className="vca-heatmap-tip-val">{cell.po_line_count.toLocaleString()}</span>
            </div>
            <div>
              <span className="vca-heatmap-tip-label">Latest end</span>
              <span className="vca-heatmap-tip-val">{formatDate(cell.latest_end)}</span>
            </div>
          </>
        )}
        <div>
          <span className="vca-heatmap-tip-label">Agency total IT</span>
          <span className="vca-heatmap-tip-val">{formatCurrency(ag.total_spend)}</span>
        </div>
        <div>
          <span className="vca-heatmap-tip-label">Component category</span>
          <span className="vca-heatmap-tip-val">{comp.category || '—'}</span>
        </div>
      </div>
      <div className="vca-heatmap-tip-foot">
        {cell ? 'Click → drill into transactions' : 'Click → seed offensive play in 30-60-90'}
      </div>
    </div>
  );
}

function Legend({ max, colorMode }) {
  return (
    <motion.div
      className="vca-heatmap-legend"
      variants={legendVariants}
      initial="hidden"
      animate="visible"
    >
      <span className="vca-heatmap-legend-label">Less</span>
      <motion.div
        className="vca-heatmap-legend-bar"
        initial={{ scaleX: 0, originX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.5, duration: 0.4, ease: 'easeOut' }}
      />
      <span className="vca-heatmap-legend-label">
        More · max {formatCurrency(max)}{colorMode === 'log' ? ' (log scale)' : colorMode === 'rank' ? ' (rank)' : ''}
      </span>
    </motion.div>
  );
}

export default PenetrationHeatmap;
